import { useState } from "react";
import {
  ActionIcon,
  AppShell,
  Box,
  Burger,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  IconActivity,
  IconAlertTriangle,
  IconArrowBackUp,
  IconAddressBook,
  IconArrowDownLeft,
  IconArrowUpRight,
  IconArrowsExchange,
  IconBuildingBank,
  IconChevronRight,
  IconCoin,
  IconCreditCard,
  IconDeviceMobile,
  IconChartBar,
  IconKey,
  IconLogout,
  IconReceipt,
  IconSearch,
  IconShieldLock,
  IconStack2,
  IconToggleRight,
  IconUsers,
  IconWallet,
} from "@tabler/icons-react";
import type { Icon } from "@tabler/icons-react";
import { useAuth } from "@/auth/useAuth";
import { entityListPath, PATHS } from "@/routes/paths";
import { searchPath } from "@/routes/paths";
import { EnvBadge, envColor } from "./EnvBanner";
import { ErrorBoundary } from "./ErrorBoundary";

interface NavItem {
  label: string;
  to: string;
  icon: Icon;
}
interface NavSection {
  group: string;
  items: NavItem[];
}

const NAV: NavSection[] = [
  { group: "Overview", items: [{ label: "Dashboard", to: PATHS.dashboard, icon: IconChartBar }] },
  {
    group: "Identity",
    items: [
      { label: "Users", to: entityListPath("user"), icon: IconUsers },
      { label: "KYC", to: entityListPath("kycVerification"), icon: IconAddressBook },
      { label: "Account deletion", to: PATHS.accountDeletions, icon: IconShieldLock },
    ],
  },
  {
    group: "Wallet",
    items: [
      { label: "Wallets", to: entityListPath("wallet"), icon: IconWallet },
      { label: "Payments", to: entityListPath("payment"), icon: IconReceipt },
      { label: "Card refunds", to: entityListPath("cardRefund"), icon: IconArrowDownLeft },
      { label: "Session Keys", to: entityListPath("sessionKey"), icon: IconKey },
      { label: "Holds", to: entityListPath("hold"), icon: IconCoin },
      { label: "Owner Ops", to: entityListPath("ownerOperation"), icon: IconKey },
      {
        label: "Smart-Account Ops",
        to: entityListPath("smartAccountOperation"),
        icon: IconActivity,
      },
      { label: "Migrations", to: entityListPath("walletMigration"), icon: IconArrowsExchange },
      // Non-terminal and wedged migrations, with the operator commands one click away.
      { label: "Stuck migrations", to: PATHS.stuckMigrations, icon: IconAlertTriangle },
      // Money that reached a migrated / retired wallet and is not forwarded yet.
      { label: "Old-wallet residuals", to: PATHS.oldWalletResiduals, icon: IconArrowBackUp },
      // v3.7 / v3.8 fleet counts and the wallets whose legacy keys answer ERC-1271.
      { label: "Contract generations", to: PATHS.generations, icon: IconStack2 },
      // Metadata only — existence + freshness of a user's encrypted owner-key backup. There is no
      // ciphertext on this surface and no recovery path behind it.
      { label: "Owner-Key Backups", to: entityListPath("ownerKeyBackup"), icon: IconShieldLock },
    ],
  },
  {
    group: "Settlement",
    items: [
      {
        label: "Settlement Obligations",
        to: entityListPath("settlementObligation"),
        icon: IconBuildingBank,
      },
      { label: "Settlement Executions", to: entityListPath("settlement"), icon: IconBuildingBank },
      { label: "Reserve Top-Up Legs", to: entityListPath("reserveTopUpLeg"), icon: IconCoin },
    ],
  },
  {
    group: "Cross-Chain",
    items: [
      { label: "Deposit Addresses", to: entityListPath("depositAddress"), icon: IconAddressBook },
      {
        label: "USDT Receive Addresses",
        to: entityListPath("stablecoinReceiveAddress"),
        icon: IconAddressBook,
      },
      {
        label: "X-Chain Deposits",
        to: entityListPath("crossChainDeposit"),
        icon: IconArrowDownLeft,
      },
      { label: "X-Chain Sends", to: entityListPath("crossChainSend"), icon: IconArrowUpRight },
    ],
  },
  {
    group: "Cards",
    items: [{ label: "Cards", to: entityListPath("card"), icon: IconCreditCard }],
  },
  {
    group: "Treasury",
    items: [
      {
        label: "Collateral Withdrawals",
        to: PATHS.collateralWithdrawals,
        icon: IconBuildingBank,
      },
    ],
  },
  {
    group: "Platform",
    items: [
      // Per-platform minimum supported app build (identity `AppVersionPolicy`). Bespoke page:
      // the edit dialog carries the role asymmetry (admin lowers, superAdmin raises).
      { label: "App versions", to: PATHS.appVersions, icon: IconDeviceMobile },
      // Runtime feature flags / maintenance kill-switches (identity `FeatureFlag`). Bespoke page:
      // per-key dialog with the availability-based superAdmin rule + vendor-wide pause / resume.
      { label: "Feature flags", to: PATHS.featureFlags, icon: IconToggleRight },
    ],
  },
  {
    group: "Ramps",
    items: [
      { label: "Deposits", to: entityListPath("deposit"), icon: IconArrowDownLeft },
      { label: "Withdrawals", to: entityListPath("withdrawal"), icon: IconArrowUpRight },
      { label: "Virtual Accounts", to: entityListPath("virtualAccount"), icon: IconBuildingBank },
      { label: "Payment Accounts", to: entityListPath("paymentAccount"), icon: IconBuildingBank },
      // The on-chain halves. Listed alongside their fiat counterparts rather than under Wallet,
      // because the question that brings an operator here ("where is my deposit?") starts on the
      // ramps side.
      { label: "Forwarder Sweeps", to: entityListPath("forwarderSweep"), icon: IconArrowsExchange },
      { label: "Off-Ramp Burns", to: entityListPath("offrampBurn"), icon: IconArrowsExchange },
    ],
  },
  {
    group: "Earn",
    items: [
      { label: "Positions", to: entityListPath("earnPosition"), icon: IconCoin },
      { label: "Operations", to: entityListPath("earnOperation"), icon: IconActivity },
      { label: "Vaults", to: entityListPath("vault"), icon: IconBuildingBank },
      { label: "Approvals", to: entityListPath("vaultApproval"), icon: IconKey },
    ],
  },
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (q.trim()) navigate(searchPath(q.trim()));
      }}
    >
      <TextInput
        leftSection={<IconSearch size={16} />}
        placeholder="Search: email · user/wallet id · address · last-four · payment id · Rain auth id"
        value={q}
        onChange={(e) => setQ(e.currentTarget.value)}
        w={{ base: 220, sm: 420 }}
      />
    </form>
  );
}

export function AppLayout() {
  const [opened, { toggle }] = useDisclosure();
  const location = useLocation();
  const { user, signOut } = useAuth();

  const isActive = (to: string) => location.pathname.startsWith(to);

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 250, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group wrap="nowrap" gap="sm">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
              <Title order={4}>Elios Backoffice</Title>
            </Link>
            <EnvBadge size="md" />
          </Group>
          <Box visibleFrom="sm">
            <GlobalSearch />
          </Box>
          <Group gap="xs" wrap="nowrap">
            <ActionIcon
              component={Link}
              to={PATHS.search}
              hiddenFrom="sm"
              aria-label="Search"
              variant="subtle"
              size="lg"
            >
              <IconSearch size={20} />
            </ActionIcon>
            <Menu shadow="md" width={220} position="bottom-end">
              <Menu.Target>
                <Tooltip label={user?.email ?? "Account"}>
                  <ActionIcon variant="subtle" size="lg" radius="xl">
                    <Text fw={700}>{(user?.email ?? "?").slice(0, 1).toUpperCase()}</Text>
                  </ActionIcon>
                </Tooltip>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{user?.email ?? user?.uid}</Menu.Label>
                <Menu.Item leftSection={<IconLogout size={16} />} onClick={() => void signOut()}>
                  Sign out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
        <Box h={3} bg={`${envColor()}.6`} />
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <AppShell.Section grow component={ScrollArea}>
          {NAV.map((section) => (
            <Box key={section.group} mb="xs">
              <Text size="xs" c="dimmed" fw={700} tt="uppercase" px="sm" pt="xs" pb={4} lts={0.4}>
                {section.group}
              </Text>
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  component={Link}
                  to={item.to}
                  label={item.label}
                  leftSection={<item.icon size={18} stroke={1.6} />}
                  rightSection={isActive(item.to) ? <IconChevronRight size={14} /> : null}
                  active={isActive(item.to)}
                  onClick={() => opened && toggle()}
                  variant="light"
                />
              ))}
            </Box>
          ))}
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </AppShell.Main>
    </AppShell>
  );
}
